import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Smartphone, Copy, Download, KeyRound, ShieldOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const inputCls = "w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/15";
const btnPrimary = "h-10 rounded-lg bg-primary px-5 text-sm font-semibold text-white inline-flex items-center gap-2 whitespace-nowrap disabled:opacity-60";
const btnGhost = "h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2 whitespace-nowrap disabled:opacity-60";
const err = (e, fb) => formatApiErrorDetail(e.response?.data?.detail) || fb;

export function MfaDialog({ onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="mfa-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Two-factor authentication</DialogTitle>
          <DialogDescription>Use an authenticator app (Google Authenticator, Microsoft Authenticator, Authy) instead of waiting for emailed codes.</DialogDescription>
        </DialogHeader>
        <MfaPanel />
      </DialogContent>
    </Dialog>
  );
}

export function MfaPanel({ forced = false, onEnrolled }) {
  const { refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(forced ? "password" : "status"); // status | password | scan | codes | disable | regen
  const [password, setPassword] = useState("");
  const [enroll, setEnroll] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auth/mfa/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ enabled: false }));
  useEffect(() => { load(); }, []);

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(err(e, "Request failed")); } finally { setBusy(false); } };
  const start = () => run(async () => {
    const { data } = await api.post("/auth/mfa/enroll/start", { password });
    setEnroll(data); setPassword(""); setCode(""); setStep("scan");
  });
  const confirm = () => run(async () => {
    const { data } = await api.post("/auth/mfa/enroll/confirm", { code });
    setCodes(data.recovery_codes); setEnroll(null); setCode(""); setStep("codes");
    toast.success("Authenticator app enabled"); if (!forced) refresh(); load();
  });
  const disable = () => run(async () => {
    await api.post("/auth/mfa/disable", { code });
    toast.success("Authenticator app turned off"); setCode(""); refresh(); await load(); setStep("status");
  });
  const regen = () => run(async () => {
    const { data } = await api.post("/auth/mfa/recovery-codes", { code });
    setCodes(data.recovery_codes); setCode(""); setStep("codes"); load();
  });
  const copyCodes = () => { navigator.clipboard?.writeText(codes.join("\n")); toast.success("Recovery codes copied"); };
  const downloadCodes = () => {
    const blob = new Blob([`Logerd recovery codes\nEach code can be used once.\n\n${codes.join("\n")}\n`], { type: "text/plain" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "logerd-recovery-codes.txt"; a.click(); URL.revokeObjectURL(a.href);
  };

  const codeInput = (placeholder = "6-digit code") => (
    <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" placeholder={placeholder} maxLength={20}
      data-testid="mfa-code-input" className={`${inputCls} text-center text-xl font-display tracking-[0.3em]`} autoFocus />
  );

  return (
    <div className="space-y-4" data-testid="mfa-panel">
        {!status && !forced && <div className="py-6 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>}

        {status && step === "status" && (
          <div className="space-y-4" data-testid="mfa-status">
            <div className={`rounded-xl border p-4 flex items-start gap-3 ${status.enabled ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
              <Smartphone className={`h-5 w-5 mt-0.5 ${status.enabled ? "text-emerald-600" : "text-slate-400"}`} />
              <div className="text-sm">
                <div className="font-semibold text-slate-900" data-testid="mfa-status-text">{status.enabled ? "Authenticator app is on" : "Authenticator app is off"}</div>
                <div className="text-slate-600">
                  {status.enabled
                    ? <>Enabled {status.enrolled_at ? new Date(status.enrolled_at).toLocaleDateString() : ""} · <span data-testid="mfa-codes-left">{status.recovery_codes_left}</span> recovery codes left</>
                    : "Sign-in codes are currently sent to your email."}
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              {status.enabled ? (
                <>
                  <button type="button" onClick={() => { setCode(""); setStep("regen"); }} className={btnGhost} data-testid="mfa-regen-btn"><RefreshCw className="h-4 w-4" /> New recovery codes</button>
                  <button type="button" onClick={() => { setCode(""); setStep("disable"); }} className={`${btnGhost} border-rose-200 text-rose-700 hover:bg-rose-50`} data-testid="mfa-disable-btn"><ShieldOff className="h-4 w-4" /> Turn off</button>
                </>
              ) : (
                <button type="button" onClick={() => setStep("password")} className={btnPrimary} data-testid="mfa-setup-btn"><Smartphone className="h-4 w-4" /> Set up authenticator app</button>
              )}
            </DialogFooter>
          </div>
        )}

        {step === "password" && (
          <form onSubmit={(e) => { e.preventDefault(); start(); }} className="space-y-3" data-testid="mfa-password-step">
            <p className="text-sm text-slate-600">Confirm your password to begin.</p>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Current password" autoComplete="current-password" autoFocus data-testid="mfa-password-input" className={inputCls} />
            <DialogFooter className="gap-2">
              {!forced && <button type="button" onClick={() => setStep("status")} className={btnGhost}>Back</button>}
              <button type="submit" disabled={busy || !password} className={btnPrimary} data-testid="mfa-password-continue">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Continue</button>
            </DialogFooter>
          </form>
        )}

        {step === "scan" && enroll && (
          <form onSubmit={(e) => { e.preventDefault(); confirm(); }} className="space-y-4" data-testid="mfa-scan-step">
            <ol className="text-sm text-slate-700 space-y-1 list-decimal pl-5">
              <li>Open your authenticator app and choose <b>Add account</b> / <b>Scan QR code</b>.</li>
              <li>Scan the code below, then enter the 6-digit code the app shows.</li>
            </ol>
            <div className="flex flex-col items-center gap-2">
              <img src={enroll.qr_data_url} alt="Scan with your authenticator app" className="h-48 w-48 rounded-lg border border-slate-200" data-testid="mfa-qr" />
              <div className="text-[11px] text-slate-500">Can't scan? Enter this key manually:</div>
              <code className="rounded bg-slate-100 px-2 py-1 text-xs font-mono tracking-wider select-all" data-testid="mfa-manual-key">{enroll.manual_key}</code>
            </div>
            {codeInput()}
            <DialogFooter className="gap-2">
              {!forced && <button type="button" onClick={() => { setEnroll(null); setStep("status"); }} className={btnGhost}>Cancel</button>}
              <button type="submit" disabled={busy || code.replace(/\D/g, "").length !== 6} className={btnPrimary} data-testid="mfa-confirm-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify & turn on</button>
            </DialogFooter>
          </form>
        )}

        {step === "codes" && (
          <div className="space-y-4" data-testid="mfa-codes-step">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2">
              <KeyRound className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div><b>Save these recovery codes now.</b> They're shown only once. If you lose your phone, any one of them signs you in. Each works a single time.</div>
            </div>
            <div className="grid grid-cols-2 gap-1.5 rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-sm" data-testid="mfa-recovery-codes">
              {codes.map((c) => <div key={c} className="px-2 py-1 bg-white rounded border border-slate-100 text-center select-all">{c}</div>)}
            </div>
            <DialogFooter className="gap-2">
              <button type="button" onClick={copyCodes} className={btnGhost} data-testid="mfa-copy-codes"><Copy className="h-4 w-4" /> Copy</button>
              <button type="button" onClick={downloadCodes} className={btnGhost} data-testid="mfa-download-codes"><Download className="h-4 w-4" /> Download</button>
              <button type="button" onClick={() => { setCodes([]); if (forced) onEnrolled?.(); else setStep("status"); }} className={btnPrimary} data-testid="mfa-codes-done">I've saved them</button>
            </DialogFooter>
          </div>
        )}

        {(step === "disable" || step === "regen") && (
          <form onSubmit={(e) => { e.preventDefault(); (step === "disable" ? disable : regen)(); }} className="space-y-3" data-testid={`mfa-${step}-step`}>
            <p className="text-sm text-slate-600">
              {step === "disable" ? "Enter the current code from your authenticator app (or a recovery code) to turn it off. Sign-in codes will go back to email." : "Enter the current code from your authenticator app (or a recovery code). Your old recovery codes will stop working."}
            </p>
            {codeInput("App code or recovery code")}
            <DialogFooter className="gap-2">
              <button type="button" onClick={() => setStep("status")} className={btnGhost}>Back</button>
              <button type="submit" disabled={busy || code.trim().length < 6} className={step === "disable" ? `${btnPrimary} bg-rose-600` : btnPrimary} data-testid={`mfa-${step}-confirm`}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} {step === "disable" ? "Turn off authenticator" : "Generate new codes"}
              </button>
            </DialogFooter>
          </form>
        )}
    </div>
  );
}
